import { Link } from '@tanstack/react-router';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RetryQueryAlert } from '@/components/alerts/retry-query-alert';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProductHoverCard } from '@/features/shared/product-hover-card';
import { cn } from '@/lib/utils';
import {
  GET_TRANSIT_KEY,
  useGetTransit,
} from '@/services/apis/queries/transits/use-get-transit';
import type { TransitDetail as TransitDetailData } from '@/types/core/transit/transit';
import { TransitPurchaseOrderDds } from './transit-purchase-order-dds';
import { TransitStatusBadge } from './transit-status-badge';

type TransitPurchaseOrderGroup = TransitDetailData['purchaseOrders'][number] & {
  lines: TransitDetailData['lines'];
};

function TransitPurchaseOrderLines({
  purchaseOrder,
  defaultOpen,
  isShipped,
  transitId,
}: {
  purchaseOrder: TransitPurchaseOrderGroup;
  defaultOpen: boolean;
  isShipped: boolean;
  transitId: string;
}) {
  const { t } = useTranslation('transits');
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const lineCountLabel = t('lineCount', {
    count: purchaseOrder.lines.length,
  });

  return (
    <Collapsible
      className='rounded-md border bg-background'
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex min-w-0 flex-col gap-1 p-4'>
          {purchaseOrder.name === null ? (
            <span className='font-medium text-sm'>
              {t('missingPurchaseOrder', { id: purchaseOrder.id })}
            </span>
          ) : (
            <Link
              className='font-medium text-primary text-sm underline hover:text-primary/80'
              params={{ id: String(purchaseOrder.id) }}
              to='/purchase-orders/$id'
            >
              {purchaseOrder.name}
            </Link>
          )}
          <span className='text-muted-foreground text-xs'>
            {purchaseOrder.supplier?.name ??
              purchaseOrder.supplier?.id ??
              t('unknownSupplier')}
          </span>
        </div>
        <CollapsibleTrigger
          aria-expanded={isOpen}
          aria-label={`${lineCountLabel}. ${t('toggleLines', { id: purchaseOrder.id })}`}
          className='flex shrink-0 items-center gap-2 p-4'
        >
          <span className='text-muted-foreground text-xs'>
            {lineCountLabel}
          </span>
          <ChevronDown
            aria-hidden
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>
      </div>
      {((isShipped && purchaseOrder.isDdsConcerned) ||
        purchaseOrder.ddsStatus !== null) && (
        <TransitPurchaseOrderDds
          canSend={isShipped && purchaseOrder.ddsCanSend}
          tracking={purchaseOrder}
          purchaseOrderId={purchaseOrder.id}
          status={purchaseOrder.ddsStatus}
          submissionUncertain={purchaseOrder.ddsSubmissionUncertain}
          transitId={transitId}
        />
      )}
      <CollapsibleContent className='px-4 pb-4'>
        {purchaseOrder.lines.length === 0 ? (
          <p className='text-muted-foreground text-sm'>{t('noLines')}</p>
        ) : (
          <div className='overflow-x-auto'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope='col'>{t('reference')}</TableHead>
                  <TableHead className='text-right' scope='col'>
                    {t('quantity')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchaseOrder.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <ProductHoverCard product={line.product} />
                    </TableCell>
                    <TableCell className='text-right'>
                      {line.quantity}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function TransitDetail({ id }: { id: string }) {
  const { t } = useTranslation('transits');
  const query = useGetTransit(id);

  if (query.isPending) {
    return <Skeleton className='h-48 w-full' />;
  }
  if (query.isError) {
    if (
      axios.isAxiosError(query.error) &&
      query.error.response?.status === 404
    ) {
      return (
        <Alert>
          <AlertDescription>
            {t('notFound')}
            <Link className='underline' search={true} to='/transits'>
              {t('backToList')}
            </Link>
          </AlertDescription>
        </Alert>
      );
    }
    return (
      <RetryQueryAlert
        message={t('detailError')}
        queryKey={[...GET_TRANSIT_KEY, id]}
      />
    );
  }

  const transit = query.data;
  const purchaseOrderGroups = new Map<number, TransitPurchaseOrderGroup>(
    transit.purchaseOrders.map((purchaseOrder) => [
      purchaseOrder.id,
      { ...purchaseOrder, lines: [] },
    ]),
  );

  for (const line of transit.lines) {
    const group = purchaseOrderGroups.get(line.purchaseOrderId);
    if (group) {
      group.lines.push(line);
    } else {
      purchaseOrderGroups.set(line.purchaseOrderId, {
        id: line.purchaseOrderId,
        name: null,
        supplier: null,
        isDdsConcerned: false,
        ddsStatus: null,
        ddsCanSend: false,
        ddsCanAmend: false,
        ddsSubmissionUncertain: false,
        ddsReferenceNumber: null,
        ddsVerificationNumber: null,
        ddsLastConsultedAt: null,
        ddsRejectionReason: null,
        ddsError: null,
        ddsHistory: [],
        ddsLastAccepted: null,
        lines: [line],
      });
    }
  }

  return (
    <div className='flex flex-col gap-4'>
      <Button asChild className='w-fit' variant='outline'>
        <Link search={true} to='/transits'>
          {t('backToList')}
        </Link>
      </Button>
      <div className='flex flex-wrap items-center gap-3'>
        <h1 className='font-semibold text-2xl'>{transit.name || transit.id}</h1>
        <TransitStatusBadge status={transit.status} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('purchaseOrders')}</CardTitle>
        </CardHeader>
        <CardContent>
          {purchaseOrderGroups.size === 0 ? (
            <p>{t('noPurchaseOrders')}</p>
          ) : (
            <ul className='flex flex-col gap-3'>
              {Array.from(purchaseOrderGroups.values()).map((purchaseOrder) => (
                <li key={purchaseOrder.id}>
                  <TransitPurchaseOrderLines
                    defaultOpen={purchaseOrderGroups.size === 1}
                    isShipped={transit.status.id === 12}
                    purchaseOrder={purchaseOrder}
                    transitId={id}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
